import { z } from 'zod';

/**
 * Validated build-time environment.
 *
 * Vite inlines `import.meta.env` at build time, so a missing variable becomes
 * `undefined` deep inside a component at runtime — usually as a blank page with
 * no useful error. Validating once here converts that into a single explicit
 * message, and gives the rest of the app a fully typed object.
 *
 * Everything here is public by definition: it is compiled into the JavaScript
 * bundle that ships to browsers.
 */

const envSchema = z.object({
  /** API origin. Empty means "use a relative path", which the dev proxy serves. */
  VITE_API_BASE_URL: z
    .string()
    .trim()
    .transform((value) => value.replace(/\/+$/, ''))
    .default(''),

  VITE_SUPABASE_URL: z
    .string()
    .trim()
    .refine((value) => value === '' || /^https:\/\/.+/.test(value), {
      message: 'VITE_SUPABASE_URL must be an https:// URL',
    })
    .default(''),

  VITE_SUPABASE_ANON_KEY: z.string().trim().default(''),
});

export type Env = z.infer<typeof envSchema> & {
  readonly isDevelopment: boolean;
  readonly isProduction: boolean;
  /** True when Supabase credentials are present and auth can be attempted. */
  readonly hasSupabase: boolean;
};

function parseEnv(): Env {
  const result = envSchema.safeParse({
    VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
    VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL,
    VITE_SUPABASE_ANON_KEY: import.meta.env.VITE_SUPABASE_ANON_KEY,
  });

  if (!result.success) {
    const problems = result.error.issues
      .map((issue) => `  • ${issue.path.join('.')}: ${issue.message}`)
      .join('\n');
    throw new Error(
      `Invalid frontend environment configuration:\n${problems}\n\n` +
        'Copy frontend/.env.example to frontend/.env.local and fill in the values.',
    );
  }

  const parsed = result.data;
  return {
    ...parsed,
    isDevelopment: import.meta.env.DEV,
    isProduction: import.meta.env.PROD,
    hasSupabase: parsed.VITE_SUPABASE_URL !== '' && parsed.VITE_SUPABASE_ANON_KEY !== '',
  };
}

export const env: Env = parseEnv();
